import React from 'react';
import { Ellipse, Line, Rect, Star, Text } from 'react-konva';
import { useMapCanvas } from '../contexts/MapCanvasContext';
import { useMapEditorUI } from '../contexts/MapEditorContext';

export const MapCanvasPreviewShape: React.FC = () => {
  const { previewShape } = useMapEditorUI();
  const { pixelsPerMeter } = useMapCanvas();

  if (!previewShape) {
    return null;
  }

  return (
    <>
      {previewShape.type === 'rectangle' && (
        <Rect
          x={previewShape.x}
          y={previewShape.y}
          width={previewShape.width}
          height={previewShape.height}
          stroke="#000"
          strokeWidth={1}
          dash={[5, 5]}
        />
      )}
      {previewShape.type === 'circle' && (
        <Ellipse
          x={previewShape.x + previewShape.width / 2}
          y={previewShape.y + previewShape.height / 2}
          radiusX={previewShape.width / 2}
          radiusY={previewShape.height / 2}
          stroke="#000"
          strokeWidth={1}
          dash={[5, 5]}
        />
      )}
      {previewShape.type === 'star' && (
        <Star
          x={previewShape.x + previewShape.width / 2}
          y={previewShape.y + previewShape.height / 2}
          numPoints={5}
          innerRadius={Math.min(previewShape.width, previewShape.height) / 4}
          outerRadius={Math.min(previewShape.width, previewShape.height) / 2}
          stroke="#000"
          strokeWidth={1}
          dash={[5, 5]}
        />
      )}
      {previewShape.type === 'zone' && (
        <Rect
          x={previewShape.x}
          y={previewShape.y}
          width={previewShape.width}
          height={previewShape.height}
          stroke="#3B82F6"
          strokeWidth={2}
          dash={[8, 4]}
          fill="#3B82F620"
          opacity={0.5}
        />
      )}
      {previewShape.type === 'wall' &&
        previewShape.x2 !== undefined &&
        previewShape.y2 !== undefined && (
          <Line
            points={[
              previewShape.x,
              previewShape.y,
              previewShape.x2,
              previewShape.y2
            ]}
            stroke="#D1D5D9"
            strokeWidth={10}
            lineCap="butt"
            dash={[5, 5]}
            opacity={0.5}
          />
        )}
      {previewShape.type === 'measure' &&
        previewShape.x2 !== undefined &&
        previewShape.y2 !== undefined &&
        (() => {
          const pixelLength = Math.hypot(
            previewShape.x2 - previewShape.x,
            previewShape.y2 - previewShape.y
          );
          const label = pixelsPerMeter
            ? `${(pixelLength / pixelsPerMeter).toFixed(2)}m (${Math.round(pixelLength)}px)`
            : `${Math.round(pixelLength)}px`;
          return (
            <>
              <Line
                points={[
                  previewShape.x,
                  previewShape.y,
                  previewShape.x2,
                  previewShape.y2
                ]}
                stroke="#F59E0B"
                strokeWidth={2}
                dash={[6, 4]}
              />
              <Text
                x={(previewShape.x + previewShape.x2) / 2}
                y={(previewShape.y + previewShape.y2) / 2 - 16}
                text={label}
                fontSize={13}
                fontFamily="Quicksand"
                fill="#B45309"
              />
            </>
          );
        })()}
      {previewShape.type === 'wall-rect' && (
        <Line
          points={[
            previewShape.x,
            previewShape.y,
            previewShape.x + previewShape.width,
            previewShape.y,
            previewShape.x + previewShape.width,
            previewShape.y + previewShape.height,
            previewShape.x,
            previewShape.y + previewShape.height
          ]}
          closed
          stroke="#D1D5D9"
          strokeWidth={10}
          lineCap="butt"
          dash={[5, 5]}
          opacity={0.5}
        />
      )}
      {previewShape.type === 'wall-path' &&
        previewShape.vertices &&
        previewShape.vertices.length > 0 &&
        previewShape.x2 !== undefined &&
        previewShape.y2 !== undefined && (
          // Match the editor's vertex-handle blue so the in-progress segment
          // reads as part of the editing UI, not as a placeholder wall.
          // Slimmer than the actual wall thickness so it doesn't visually
          // pre-commit to the final stroke.
          <Line
            points={[
              previewShape.vertices[previewShape.vertices.length - 1].x,
              previewShape.vertices[previewShape.vertices.length - 1].y,
              previewShape.x2,
              previewShape.y2
            ]}
            stroke="#2563eb"
            strokeWidth={3}
            lineCap="butt"
            dash={[5, 5]}
            opacity={0.9}
          />
        )}

      {/* Snap alignment guide lines */}
      {previewShape.snapAlignX !== undefined && (
        <Line
          x={previewShape.snapAlignX}
          points={[0, -5000, 0, 5000]}
          stroke="#FF4D94"
          strokeWidth={1}
          dash={[6, 4]}
          listening={false}
        />
      )}
      {previewShape.snapAlignY !== undefined && (
        <Line
          y={previewShape.snapAlignY}
          points={[-5000, 0, 5000, 0]}
          stroke="#FF4D94"
          strokeWidth={1}
          dash={[6, 4]}
          listening={false}
        />
      )}
      {/* Snap point indicator */}
      {previewShape.snapAlignX !== undefined && previewShape.snapAlignY !== undefined && (
        <Ellipse
          x={previewShape.snapAlignX}
          y={previewShape.snapAlignY}
          radiusX={4}
          radiusY={4}
          fill="#FF4D94"
          listening={false}
        />
      )}
      {/* Attach point indicator: cursor snapped onto a wall vertex or segment */}
      {previewShape.snapTarget && (
        <Ellipse
          x={previewShape.snapTarget.x}
          y={previewShape.snapTarget.y}
          radiusX={5}
          radiusY={5}
          stroke="#FF4D94"
          strokeWidth={1.5}
          fill="#FFFFFF"
          listening={false}
        />
      )}
    </>
  );
};
